import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Text from '~/shared/components/Text/index.js';
import { PersonalizationTypes } from '~/shared/utils/embroidery';
import { mobile, Separator } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

const EmbroideryDetails = styled.div`
  flex: 0 1 90px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: ${({ theme }) => theme.colors.textDeemphasizedAlternate};

  ${mobile`
    margin-bottom: 40px;
  `}
`;

const EmbroideryDetailLineItem = styled.div`
  display: flex;
  justify-content: space-between;
`;

const EmbroideryDetailText = styled(Text).attrs({ variant: 'bodyMed' })``;

const TotalText = styled(EmbroideryDetailText)`
  font-weight: 600;
`;

const TotalAmount = styled(EmbroideryDetailText)`
  color: ${({ theme }) => theme.colors.textDefault};
  font-weight: 700;
`;

const separatorStyles = css`
  margin: 0;
`;

const EmbroideryDetailSection = ({
  hasIconCustomizations,
  hasTextCustomizations,
  prices = {},
}) => {
  const total = useMemo(() => {
    let amount = 0;

    if (hasTextCustomizations) {
      amount += prices[PersonalizationTypes.TEXT];
    }

    if (hasIconCustomizations) {
      amount += prices[PersonalizationTypes.LOGO];
    }

    return amount.toFixed(2);
  }, [hasIconCustomizations, hasTextCustomizations, prices]);

  return (
    <EmbroideryDetails>
      {hasTextCustomizations && (
        <EmbroideryDetailLineItem>
          <EmbroideryDetailText>
            <FormattedMessage
              id="site_pdp.embroidery_custom_text_line_item"
              defaultMessage="Custom Text"
            />
            {': '}
          </EmbroideryDetailText>
          <EmbroideryDetailText>{`$${prices[PersonalizationTypes.TEXT].toFixed(
            2
          )}`}</EmbroideryDetailText>
        </EmbroideryDetailLineItem>
      )}

      {hasIconCustomizations && (
        <EmbroideryDetailLineItem>
          <EmbroideryDetailText>
            <FormattedMessage
              id="site_pdp.embroidery_sleeve_tag_line_item"
              defaultMessage="Sleeve Tag"
            />
            {': '}
          </EmbroideryDetailText>
          <EmbroideryDetailText>{`$${prices[PersonalizationTypes.LOGO].toFixed(
            2
          )}`}</EmbroideryDetailText>
        </EmbroideryDetailLineItem>
      )}

      <Separator
        thickness={1}
        separatorStyle={separatorStyles}
        color="textDeemphasizedAlternate"
      />
      <EmbroideryDetailLineItem>
        <TotalText>
          <FormattedMessage
            id="site_pdp.embroidery_subtotal"
            defaultMessage="Personalization Subtotal"
          />
          {': '}
        </TotalText>
        <TotalAmount data-testid="total">{`$${total}`}</TotalAmount>
      </EmbroideryDetailLineItem>
    </EmbroideryDetails>
  );
};

EmbroideryDetailSection.propTypes = {
  hasIconCustomizations: PropTypes.bool.isRequired,
  hasTextCustomizations: PropTypes.bool.isRequired,
  prices: PropTypes.shape({
    [PersonalizationTypes.TEXT]: PropTypes.number,
    [PersonalizationTypes.LOGO]: PropTypes.number,
  }),
};

export default EmbroideryDetailSection;
